import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  UIExample,
  MockDashboard,
  MockCalendar,
  MockReservationForm,
  MockSubscriptions,
  MockCalendarIntegration,
} from './docs';

interface Section {
  id: string;
  title: string;
  icon: React.ReactNode;
}

const sections: Section[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    id: 'calendar',
    title: 'Calendar & Reservations',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    id: 'external-calendar',
    title: 'External Calendar Sync',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
  },
  {
    id: 'subscriptions',
    title: 'Subscriptions',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    id: 'account',
    title: 'Account Management',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
  },
  {
    id: 'admin',
    title: 'Admin Features',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

const Documentation: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link to="/login" className="flex items-center gap-2">
                <svg className="w-8 h-8 text-blue-600" viewBox="0 0 32 32" fill="none">
                  <rect x="2" y="4" width="28" height="26" rx="4" fill="#DBEAFE" />
                  <rect x="2" y="4" width="28" height="8" rx="4" fill="#3B82F6" />
                  <rect x="8" y="2" width="3" height="6" rx="1.5" fill="#1E40AF" />
                  <rect x="21" y="2" width="3" height="6" rx="1.5" fill="#1E40AF" />
                  <text x="16" y="24" fontFamily="Arial" fontSize="14" fontWeight="bold" fill="#3B82F6" textAnchor="middle">R</text>
                </svg>
                <span className="text-xl font-bold text-gray-900">ReserveFlow</span>
              </Link>
              <span className="text-gray-300">|</span>
              <span className="text-gray-600 font-medium">Documentation</span>
            </div>

            <Link
              to="/login"
              className="text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              Back to App
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:flex lg:gap-8">
          {/* Mobile TOC Toggle */}
          <div className="lg:hidden mb-6">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-200"
            >
              <span className="font-medium text-gray-900">Table of Contents</span>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {mobileMenuOpen && (
              <nav className="mt-2 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                  >
                    <span className="text-gray-400">{section.icon}</span>
                    <span className="text-sm font-medium">{section.title}</span>
                  </button>
                ))}
              </nav>
            )}
          </div>

          {/* Desktop Sidebar */}
          <aside className="hidden lg:block lg:w-64 lg:flex-shrink-0">
            <nav className="sticky top-24 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Contents
              </h3>
              <ul className="space-y-1">
                {sections.map((section) => (
                  <li key={section.id}>
                    <button
                      onClick={() => scrollToSection(section.id)}
                      className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <span className="text-gray-400">{section.icon}</span>
                      <span className="text-sm font-medium">{section.title}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 sm:p-8 mb-8 text-white">
              <h1 className="text-2xl sm:text-3xl font-bold mb-3">
                Welcome to ReserveFlow
              </h1>
              <p className="text-blue-100 text-lg">
                A smart reservation management system with seamless multi-day booking capabilities
                and external calendar integration.
              </p>
            </div>

            {/* Getting Started Section */}
            <section id="getting-started" className="mb-12 scroll-mt-24">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                    {sections[0].icon}
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Getting Started</h2>
                </div>

                <div className="prose prose-gray max-w-none">
                  <h3 className="text-lg font-semibold text-gray-900 mt-0">What is ReserveFlow?</h3>
                  <p className="text-gray-600">
                    ReserveFlow is a shared reservation calendar that helps teams manage bookings for
                    shared resources like meeting rooms, equipment, or any other reservable items.
                    All users can view all reservations, making it easy to coordinate schedules.
                  </p>

                  <h3 className="text-lg font-semibold text-gray-900 mt-6">Logging In</h3>
                  <p className="text-gray-600">
                    To access ReserveFlow, you'll need an account created by an administrator.
                    Once you have your credentials, simply enter your email and password on the
                    login page to access your dashboard.
                  </p>

                  <h3 className="text-lg font-semibold text-gray-900 mt-6">Your Dashboard</h3>
                  <p className="text-gray-600 mb-4">
                    After logging in, you'll see your personalized dashboard with quick access to
                    the calendar, your reservation statistics, and upcoming reservations.
                  </p>
                </div>

                <UIExample
                  title="Dashboard Overview"
                  description="Your home screen after logging in, showing quick stats and upcoming reservations."
                  device="phone"
                >
                  <MockDashboard />
                </UIExample>
              </div>
            </section>

            {/* Calendar Section */}
            <section id="calendar" className="mb-12 scroll-mt-24">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-green-100 rounded-lg text-green-600">
                    {sections[1].icon}
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Calendar & Reservations</h2>
                </div>

                <div className="prose prose-gray max-w-none">
                  <h3 className="text-lg font-semibold text-gray-900 mt-0">Viewing the Calendar</h3>
                  <p className="text-gray-600">
                    The calendar displays a monthly view showing all reservations from all users.
                    Use the arrow buttons to navigate between months. Each day cell shows
                    reservation titles, and multi-day reservations span across multiple cells.
                  </p>
                  <ul className="text-gray-600 list-disc pl-5 space-y-1">
                    <li><strong>Today's date</strong> is highlighted with a blue background</li>
                    <li><strong>Reservations</strong> appear as colored bars within each day</li>
                    <li><strong>Multi-day events</strong> span across multiple days with connected bars</li>
                    <li><strong>All-day events</strong> are shown in indigo/purple, timed events in blue</li>
                  </ul>
                </div>

                <UIExample
                  title="Calendar View"
                  description="Monthly calendar showing reservations. Click any date to create a new reservation, or click an existing reservation to view details."
                  device="phone"
                >
                  <MockCalendar />
                </UIExample>

                <div className="prose prose-gray max-w-none mt-8">
                  <h3 className="text-lg font-semibold text-gray-900">Creating a Reservation</h3>
                  <p className="text-gray-600">
                    To create a new reservation:
                  </p>
                  <ol className="text-gray-600 list-decimal pl-5 space-y-2">
                    <li>Click the <strong>"Create Reservation"</strong> button at the bottom of the calendar</li>
                    <li>Or click directly on any date in the calendar</li>
                    <li>Fill in the reservation details:
                      <ul className="list-disc pl-5 mt-1 space-y-1">
                        <li><strong>Title:</strong> A descriptive name for the reservation (required)</li>
                        <li><strong>Description:</strong> Optional additional details</li>
                        <li><strong>All Day Event:</strong> Check this for full-day reservations</li>
                        <li><strong>Start/End Date & Time:</strong> For timed events, select dates and times</li>
                        <li><strong>End Date:</strong> For all-day events, optionally set an end date for multi-day reservations</li>
                      </ul>
                    </li>
                    <li>Click <strong>"Create"</strong> to save your reservation</li>
                  </ol>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <UIExample
                    title="Creating a New Reservation"
                    description="The form for creating a new timed reservation."
                    device="phone"
                  >
                    <MockReservationForm mode="create" />
                  </UIExample>

                  <UIExample
                    title="All-Day Event"
                    description="Toggle 'All day' for reservations spanning entire days."
                    device="phone"
                  >
                    <MockReservationForm mode="create" isAllDay={true} />
                  </UIExample>
                </div>

                <div className="prose prose-gray max-w-none mt-8">
                  <h3 className="text-lg font-semibold text-gray-900">Editing & Deleting</h3>
                  <p className="text-gray-600">
                    You can edit or delete your own reservations by clicking on them in the calendar.
                    Note that you can only modify reservations you created, unless you're an administrator.
                  </p>
                </div>

                <UIExample
                  title="Editing a Reservation"
                  description="Click on your reservation to edit or delete it."
                  device="desktop"
                >
                  <MockReservationForm mode="edit" />
                </UIExample>
              </div>
            </section>

            {/* External Calendar Section */}
            <section id="external-calendar" className="mb-12 scroll-mt-24">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-purple-100 rounded-lg text-purple-600">
                    {sections[2].icon}
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">External Calendar Sync</h2>
                </div>

                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-600">
                    ReserveFlow provides an iCal feed URL that you can subscribe to in your favorite
                    calendar application. This allows you to see your reservations alongside your
                    other appointments in Google Calendar, Apple Calendar, Outlook, and more.
                  </p>

                  <h3 className="text-lg font-semibold text-gray-900 mt-6">Getting Your Feed URL</h3>
                  <ol className="text-gray-600 list-decimal pl-5 space-y-2">
                    <li>Go to your <strong>Dashboard</strong></li>
                    <li>Click <strong>"Manage Subscriptions"</strong></li>
                    <li>Find the <strong>"Calendar Feed"</strong> section</li>
                    <li>Copy your personal iCal feed URL</li>
                  </ol>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                    <p className="text-yellow-800 text-sm">
                      <strong>Important:</strong> Your feed URL is private. Don't share it with others
                      as it provides access to your reservation data.
                    </p>
                  </div>
                </div>

                <UIExample
                  title="Calendar Integration Options"
                  description="Subscribe to your reservations in external calendar apps."
                  device="phone"
                >
                  <MockCalendarIntegration />
                </UIExample>

                <div className="prose prose-gray max-w-none mt-8">
                  <h3 className="text-lg font-semibold text-gray-900">Adding to Google Calendar</h3>
                  <ol className="text-gray-600 list-decimal pl-5 space-y-2">
                    <li>Open <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Calendar</a></li>
                    <li>Click the <strong>+</strong> next to "Other calendars"</li>
                    <li>Select <strong>"From URL"</strong></li>
                    <li>Paste your iCal feed URL</li>
                    <li>Click <strong>"Add calendar"</strong></li>
                  </ol>

                  <h3 className="text-lg font-semibold text-gray-900 mt-6">Adding to Apple Calendar</h3>
                  <ol className="text-gray-600 list-decimal pl-5 space-y-2">
                    <li>Open the <strong>Calendar</strong> app</li>
                    <li>Go to <strong>File &gt; New Calendar Subscription</strong></li>
                    <li>Paste your iCal feed URL</li>
                    <li>Click <strong>Subscribe</strong></li>
                    <li>Choose your sync preferences and click <strong>OK</strong></li>
                  </ol>

                  <h3 className="text-lg font-semibold text-gray-900 mt-6">Adding to Outlook</h3>
                  <ol className="text-gray-600 list-decimal pl-5 space-y-2">
                    <li>Open <strong>Outlook Calendar</strong></li>
                    <li>Click <strong>"Add calendar"</strong> in the sidebar</li>
                    <li>Select <strong>"Subscribe from web"</strong></li>
                    <li>Paste your iCal feed URL</li>
                    <li>Name the calendar and click <strong>Import</strong></li>
                  </ol>
                </div>
              </div>
            </section>

            {/* Subscriptions Section */}
            <section id="subscriptions" className="mb-12 scroll-mt-24">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-orange-100 rounded-lg text-orange-600">
                    {sections[3].icon}
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Subscriptions</h2>
                </div>

                <div className="prose prose-gray max-w-none">
                  <h3 className="text-lg font-semibold text-gray-900 mt-0">What Are Subscriptions?</h3>
                  <p className="text-gray-600">
                    Subscriptions control which reservations appear in your external calendar feed
                    (Google Calendar, Apple Calendar, etc.). While you can always see all
                    reservations in the ReserveFlow app, subscriptions let you filter what syncs
                    to your external calendars.
                  </p>

                  <h3 className="text-lg font-semibold text-gray-900 mt-6">Managing Subscriptions</h3>
                  <p className="text-gray-600">
                    Access subscription management from your Dashboard by clicking
                    <strong> "Manage Subscriptions"</strong>. You can:
                  </p>
                  <ul className="text-gray-600 list-disc pl-5 space-y-1">
                    <li>Subscribe to individual users to see their reservations</li>
                    <li>Use <strong>"Subscribe to All"</strong> to automatically include all users</li>
                    <li>Unsubscribe from users you don't need to track</li>
                  </ul>
                </div>

                <UIExample
                  title="Subscription Manager"
                  description="Choose whose reservations appear in your external calendar feed."
                  device="phone"
                >
                  <MockSubscriptions />
                </UIExample>

                <div className="prose prose-gray max-w-none mt-8">
                  <h3 className="text-lg font-semibold text-gray-900">Subscribe to All</h3>
                  <p className="text-gray-600">
                    Enable <strong>"Subscribe to All Users"</strong> to automatically include
                    reservations from all users, including any new users added in the future.
                    This is useful if you want a complete view of all reservations across your
                    organization.
                  </p>
                </div>
              </div>
            </section>

            {/* Account Section */}
            <section id="account" className="mb-12 scroll-mt-24">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-teal-100 rounded-lg text-teal-600">
                    {sections[4].icon}
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Account Management</h2>
                </div>

                <div className="prose prose-gray max-w-none">
                  <h3 className="text-lg font-semibold text-gray-900 mt-0">Changing Your Password</h3>
                  <p className="text-gray-600">
                    To change your password:
                  </p>
                  <ol className="text-gray-600 list-decimal pl-5 space-y-2">
                    <li>Go to your <strong>Dashboard</strong></li>
                    <li>Click <strong>"Change Password"</strong> in the Account section</li>
                    <li>Enter your current password for verification</li>
                    <li>Enter your new password (minimum 8 characters)</li>
                    <li>Confirm your new password</li>
                    <li>Click <strong>"Change Password"</strong> to save</li>
                  </ol>

                  <h3 className="text-lg font-semibold text-gray-900 mt-6">Account Information</h3>
                  <p className="text-gray-600">
                    Your dashboard displays your account information including your name, email,
                    and role. Contact an administrator if you need to update your name or email
                    address.
                  </p>

                  <h3 className="text-lg font-semibold text-gray-900 mt-6">Signing Out</h3>
                  <p className="text-gray-600">
                    Click the <strong>"Sign Out"</strong> button in the top-right corner of your
                    dashboard to securely log out of your account.
                  </p>
                </div>
              </div>
            </section>

            {/* Admin Section */}
            <section id="admin" className="mb-12 scroll-mt-24">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-red-100 rounded-lg text-red-600">
                    {sections[5].icon}
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Admin Features</h2>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-blue-800 text-sm">
                    <strong>Note:</strong> This section is only relevant for users with administrator privileges.
                  </p>
                </div>

                <div className="prose prose-gray max-w-none">
                  <h3 className="text-lg font-semibold text-gray-900 mt-0">Admin Panel Access</h3>
                  <p className="text-gray-600">
                    Administrators have access to an Admin Panel from their dashboard. This panel
                    provides tools for managing users and system settings.
                  </p>

                  <h3 className="text-lg font-semibold text-gray-900 mt-6">Managing Users</h3>
                  <p className="text-gray-600">
                    As an administrator, you can:
                  </p>
                  <ul className="text-gray-600 list-disc pl-5 space-y-1">
                    <li><strong>Create new users:</strong> Add team members with email, name, and initial password</li>
                    <li><strong>Edit users:</strong> Update user information and roles</li>
                    <li><strong>Manage subscriptions:</strong> Set up which users each person is subscribed to</li>
                    <li><strong>Deactivate users:</strong> Disable accounts that are no longer needed</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-900 mt-6">Creating New Accounts</h3>
                  <p className="text-gray-600">
                    Public registration is disabled for security. Only administrators can create
                    new user accounts through the Admin Panel. When creating a user, you can:
                  </p>
                  <ul className="text-gray-600 list-disc pl-5 space-y-1">
                    <li>Set their initial password (they can change it later)</li>
                    <li>Choose their role (User or Admin)</li>
                    <li>Enable "Subscribe to All" by default</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-900 mt-6">User Subscriptions Management</h3>
                  <p className="text-gray-600">
                    Administrators can manage subscription settings for any user, helping set up
                    new team members with the right calendar feed configuration from the start.
                  </p>
                </div>
              </div>
            </section>

            {/* Help Section */}
            <section className="mb-8">
              <div className="bg-gradient-to-r from-gray-700 to-gray-800 rounded-xl p-6 sm:p-8 text-white">
                <h2 className="text-xl font-bold mb-3">Need More Help?</h2>
                <p className="text-gray-300 mb-4">
                  If you have questions or need assistance, please contact your system administrator.
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-900 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                >
                  Go to Login
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Documentation;
